import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ShieldCheck, Loader2 } from "lucide-react";

const BRANCHES = [
  ["army", "Army"], ["navy", "Navy"], ["air_force", "Air Force"], ["marines", "Marine Corps"],
  ["coast_guard", "Coast Guard"], ["space_force", "Space Force"], ["first_responder", "First Responder"],
];

export function VeteranVerifyDialog({ open, setOpen }) {
  const { refreshUser } = useAuth();
  const [branch, setBranch] = useState("");
  const [idLast4, setIdLast4] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!branch) { toast.error("Select your branch of service"); return; }
    if (idLast4.length !== 4) { toast.error("Enter the last 4 digits of your service/ID number"); return; }
    if (year.length !== 4) { toast.error("Enter your service start year"); return; }
    setBusy(true);
    try {
      await api.post("/veteran/verify", { branch, id_last4: idLast4, service_start_year: parseInt(year, 10) });
      await refreshUser();
      toast.success("Veteran status verified — 15% discount unlocked!");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="veteran-verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> Verify veteran / first responder
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">Quick check to unlock your 15% first-cleaning discount. We only store your branch and the last 4 digits of your ID.</p>
        <div className="space-y-4 py-2">
          <div>
            <Label>Branch of service</Label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="verify-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {BRANCHES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="idl4">Last 4 of service / ID number</Label>
            <Input id="idl4" data-testid="verify-id-last4" value={idLast4} inputMode="numeric"
              onChange={(e) => setIdLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-1.5 h-11" placeholder="1234" />
          </div>
          <div>
            <Label htmlFor="svcyr">Service start year</Label>
            <Input id="svcyr" data-testid="verify-year" value={year} inputMode="numeric"
              onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-1.5 h-11" placeholder="2008" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="verify-submit" className="bg-emerald-700 hover:bg-emerald-800 gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
